import { useEffect, useState } from "react";
import { apiFetch } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";

// ADR-0207 §3.5 (pre-cutover wording): the hub's own AI credentials.
const HUB_AI_CREDENTIALS: ReadonlySet<string> = new Set(["anthropic-api-key", "openai-api-key"]);
const HUB_AI_LANE_LABEL =
  "Hub AI — used for Atlas's own evaluation runs and hub admin tools, and, until every workspace connects its own provider, for workspaces without one. Never used for a workspace that has connected its own provider.";

// Mirrors services/hub/src/secrets/service.ts (AtlasVaultStatus / ManagedSecretStatus).
type SecretStatusValue = "configured_db" | "configured_env" | "not_configured";

type ManagedSecretStatus = {
  name: string;
  envVar: string;
  label: string;
  status: SecretStatusValue;
  requiresRestart: boolean;
  bootstrapOnly: boolean;
  canGenerate: boolean;
  updatedAt: string | null;
  updatedByEmail: string | null;
};

type AtlasVaultStatus = {
  encryptionKeyConfigured: boolean;
  secrets: ManagedSecretStatus[];
};

/**
 * ADR-0060: `applied: true` means the new value is live in the Hub's process
 * right now — no restart needed. `applied: false` means either requiresRestart
 * is true OR the live-refresh failed (DB/decrypt error); the user should restart.
 */
type UpdateSecretResponse = { ok: true; requiresRestart: boolean; applied: boolean };

const DATABASE_URL_NAME = "database-url";
const INITIAL_ADMIN_PASSWORD_NAME = "initial-admin-password";

function statusPill(status: SecretStatusValue) {
  switch (status) {
    case "configured_db":
      return <span className="pill pillOk">In database</span>;
    case "configured_env":
      return <span className="pill">From .env (fallback)</span>;
    case "not_configured":
      return <span className="pill pillWarn">Not configured</span>;
  }
}

function formatUpdatedAt(updatedAt: string | null, updatedByEmail: string | null): string {
  if (!updatedAt) return "—";
  const date = new Date(updatedAt);
  const dateStr = isNaN(date.getTime()) ? updatedAt : date.toLocaleString();
  return updatedByEmail ? `${dateStr} by ${updatedByEmail}` : dateStr;
}

export function AdminSecrets() {
  const [data, setData] = useState<AtlasVaultStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-row state, keyed by secret name.
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  // Persistent banner shown after a successful save/generate of a requiresRestart secret.
  const [restartBanner, setRestartBanner] = useState(false);

  async function refresh(opts?: { silent?: boolean }) {
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<AtlasVaultStatus>("/api/admin/hub-secrets");
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }

  async function saveSecret(name: string) {
    const value = inputs[name];
    if (!value || value.trim().length === 0) {
      setRowErrors((prev) => ({ ...prev, [name]: "Value is required." }));
      return;
    }
    setSaving((prev) => ({ ...prev, [name]: true }));
    setRowErrors((prev) => ({ ...prev, [name]: "" }));
    try {
      const res = await apiFetch<UpdateSecretResponse>(`/api/admin/hub-secrets/${encodeURIComponent(name)}`, {
        method: "PUT",
        body: { value }
      });
      setInputs((prev) => ({ ...prev, [name]: "" }));
      // ADR-0060: show the banner only when the value is NOT live yet —
      // requiresRestart: true entries AND refresh-failure fallbacks both set applied=false.
      if (!res.applied) setRestartBanner(true);
      await refresh({ silent: true });
    } catch (e) {
      setRowErrors((prev) => ({ ...prev, [name]: e instanceof Error ? e.message : String(e) }));
    } finally {
      setSaving((prev) => ({ ...prev, [name]: false }));
    }
  }

  async function generateSecret(name: string) {
    setSaving((prev) => ({ ...prev, [name]: true }));
    setRowErrors((prev) => ({ ...prev, [name]: "" }));
    try {
      const res = await apiFetch<UpdateSecretResponse>(
        `/api/admin/hub-secrets/${encodeURIComponent(name)}/generate`,
        { method: "POST" }
      );
      // ADR-0060: show the banner only when the value is NOT live yet —
      // requiresRestart: true entries AND refresh-failure fallbacks both set applied=false.
      if (!res.applied) setRestartBanner(true);
      await refresh({ silent: true });
    } catch (e) {
      setRowErrors((prev) => ({ ...prev, [name]: e instanceof Error ? e.message : String(e) }));
    } finally {
      setSaving((prev) => ({ ...prev, [name]: false }));
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const secrets = data?.secrets ?? [];

  return (
    <div>
      <PageLayout
        title="Atlas Vault"
        description="Hub-level managed secrets, encrypted at rest in hub_config_secrets (ADR-0046). Values are write-only — Atlas Vault never displays existing values."
      >
        {error && <ErrorState detail={error} />}
        {loading && <LoadingState />}

        {data && !data.encryptionKeyConfigured && (
          <div className="card" style={{ marginTop: 12, borderLeft: "4px solid var(--warn-text, #c97500)" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>
              ⚠️ HUB_CONFIG_ENCRYPTION_KEY is not set
            </div>
            <div className="help">
              Atlas Vault is running in fallback mode. All managed secrets are read from <span className="mono">.env</span>.
              You cannot save new values until the master key is configured. Generate one with{" "}
              <span className="mono">openssl rand -base64 32</span>, set it as <span className="mono">HUB_CONFIG_ENCRYPTION_KEY</span>{" "}
              in the Hub environment, and restart Hub.
            </div>
          </div>
        )}

        {restartBanner && (
          <div className="card" style={{ marginTop: 12, borderLeft: "4px solid var(--warn-text, #c97500)" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Restart required</div>
            <div className="help">
              A secret marked as restart-required was updated. Restart Hub for the new value to take effect.
              For the session signing key, all active user sessions will be invalidated at restart.
            </div>
          </div>
        )}

        <div className="card tableWrap" style={{ marginTop: 12 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Secret</th>
                <th>Status</th>
                <th>Last updated</th>
                <th style={{ width: "30%" }}>New value</th>
                <th style={{ width: 200 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {secrets.map((s) => {
                const isDatabaseUrl = s.name === DATABASE_URL_NAME;
                const isInitialAdmin = s.name === INITIAL_ADMIN_PASSWORD_NAME;
                const inputDisabled = isDatabaseUrl || isInitialAdmin || !data?.encryptionKeyConfigured || saving[s.name];
                const helperText = isDatabaseUrl
                  ? "Set in Hub environment only. Cannot be changed via the UI."
                  : isInitialAdmin
                    ? "Bootstrap only — deleted after the first hub admin is seeded."
                    : null;
                return (
                  <tr key={s.name}>
                    <td>
                      <div style={{ fontWeight: 800 }}>
                        {s.label}
                        {s.requiresRestart && (
                          <span
                            className="pill pillWarn"
                            aria-label={
                              s.name === "session-key-hex"
                                ? "Updating this value invalidates all active sessions at next Hub restart."
                                : "Updating this value requires a Hub restart to take effect."
                            }
                            style={{ marginLeft: 8, fontSize: 11 }}
                          >
                            restart required
                          </span>
                        )}
                      </div>
                      <div className="muted mono" style={{ fontSize: 12 }}>
                        {s.envVar}
                      </div>
                      {HUB_AI_CREDENTIALS.has(s.name) ? (
                        <div className="muted" style={{ fontSize: 12 }}>{HUB_AI_LANE_LABEL}</div>
                      ) : null}
                    </td>
                    <td>{statusPill(s.status)}</td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {formatUpdatedAt(s.updatedAt, s.updatedByEmail)}
                    </td>
                    <td>
                      <input
                        type="password"
                        className="input"
                        autoComplete="off"
                        placeholder={inputDisabled && !saving[s.name] ? "—" : "Enter new value"}
                        aria-label={`New value for ${s.label}`}
                        value={inputs[s.name] ?? ""}
                        disabled={inputDisabled}
                        onChange={(e) => setInputs((prev) => ({ ...prev, [s.name]: e.target.value }))}
                      />
                      {helperText && <div className="help">{helperText}</div>}
                      {rowErrors[s.name] && <div className="help">{rowErrors[s.name]}</div>}
                    </td>
                    <td>
                      <div className="actions" style={{ justifyContent: "flex-start", flexWrap: "wrap", gap: 6 }}>
                        <button
                          className="btn btnPrimary"
                          type="button"
                          disabled={inputDisabled || !(inputs[s.name] && inputs[s.name].trim().length > 0)}
                          onClick={() => void saveSecret(s.name)}
                        >
                          {saving[s.name] ? "Saving…" : "Save"}
                        </button>
                        {s.canGenerate && !isDatabaseUrl && !isInitialAdmin && (
                          <button
                            className="btn"
                            type="button"
                            disabled={!data?.encryptionKeyConfigured || saving[s.name]}
                            onClick={() => void generateSecret(s.name)}
                            aria-label={`Generate a new random value for ${s.label}`}
                          >
                            Generate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && !error && secrets.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted" style={{ padding: 12 }}>
                    No managed secrets defined.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="card" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>About Atlas Vault</div>
          <div className="help">
            Managed secrets are encrypted with AES-256-GCM and stored in Postgres. The master key
            (<span className="mono">HUB_CONFIG_ENCRYPTION_KEY</span>) lives in the Hub environment.
            Values are never displayed in this UI or returned by any API — saving a new value writes
            it directly into the encrypted store. Most secrets take effect on the next Hub restart.
          </div>
          <div className="help" style={{ marginTop: 6 }}>
            To add a new managed secret, edit <span className="mono">services/hub/src/secrets/registry.ts</span>{" "}
            and restart Hub. A new row will appear here automatically.
          </div>
        </div>
      </PageLayout>
    </div>
  );
}
